/* eslint-disable max-len */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { mapObject, constant, noop, find, contains, isEmpty } from 'underscore';
import { Dropdown, TextField } from '@fluentui/react';
import { DropdownTreeViewSelector } from '@bingads-webui-react/dropdown-tree-view-selector';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getContainerFactoryStyles } from './style';
import { types, Types, getInitTreeNodes, buildTreeNodesFromValueOptions, buildTreeSelectionData, TreeConditionKeyDeepnessKeyMapping } from '../util/index';

const FilterOptionsItemUnstyled = (props) => {
  const {
    classes: {
      Container,
      OperandContainer,
      OperatorContainer,
      OperandValueContainer,
      DeleteButtonContainer,
      TextArea,
    },
    i18nLegacy,
    keyOptions,
    valueOptions,
    operatorOptions,
    keyOptionsDefaultSelectedKey,
    valueOptionsDefaultSelectedKey,
    operatorDefaultSelectedKey,
    CollectData,
    type,
    disabled,
    deletable,
    itemKey,
    onFilterOptionsItemDeleted,
    errorMessage,
    dataModel,
    enableOperator,
    enableMultiSelect,
  } = props;

  const [selectedKey, setSelectedKey] = React.useState();
  const [selectedValue, setSelectedValueItem] = React.useState();
  const [textValue, setTextValue] = React.useState();
  const [selectedOperator, setSelectedOperator] = React.useState();
  // an array of values for multiselect
  const [selectedValueKeys, setSelectedValues] = React.useState();
  const [treeNodes, setTreeNodes] = React.useState(() => getInitTreeNodes(type, valueOptions));
  const [seletedNodes, setSelectedNodes] = React.useState([]);
  const deepnessKeyMapping = useRef({});

  const getKeys = useCallback((valueUpdate) => {
    if (dataModel && dataModel.data.valueKeys) {
      const newKeys = [...dataModel.data.valueKeys];
      if (valueUpdate.selected !== false) {
        if (!contains(newKeys, valueUpdate.key)) {
          newKeys.push(valueUpdate.key);
        }
        return newKeys;
      }
      return newKeys.filter(e => e !== valueUpdate.key);
    } else if (valueUpdate.selected !== false) {
      return [valueUpdate.key];
    }
    return [];
  }, [dataModel]);

  /**
   * Collect data to parent component
   */
  useEffect(() => {
    const defaultSelectedKeyOption =
      find(keyOptions, i => i.key === keyOptionsDefaultSelectedKey);
    const defaultSelectedValueOption =
      find(valueOptions, i => i.key === valueOptionsDefaultSelectedKey);
    const defaultSelectedOperator =
      find(operatorOptions, i => i.key ===
        (dataModel.data ? dataModel.data.operator : operatorDefaultSelectedKey));
    const data = {
      key: (selectedKey && selectedKey.key) || itemKey,
      value: (selectedValue ? selectedValue.key : null) || textValue,
      operator: (selectedOperator && selectedOperator.key) ||
        (defaultSelectedOperator ? defaultSelectedOperator.key : null),
      keyText: (selectedKey && selectedKey.text) ||
        (defaultSelectedKeyOption ? defaultSelectedKeyOption.text : null),
      // eslint-disable-next-line no-nested-ternary
      valueText: selectedValue ? selectedValue.text :
        (defaultSelectedValueOption ? defaultSelectedValueOption.text : null),
      operatorText: (selectedOperator && selectedOperator.text) ||
        (defaultSelectedOperator ? defaultSelectedOperator.text : null),
      valueKeys: selectedValue ? getKeys(selectedValue) : [],
    };
    if (type === Types.TreeSelection) {
      const value = seletedNodes.length > 0
        ? seletedNodes[seletedNodes.length - 1].key
        : null;
      data.value = value;
      data.valueText = value;
      // used for preview display
      data.valueKeys = seletedNodes.map(n => n.key);
      deepnessKeyMapping.current = TreeConditionKeyDeepnessKeyMapping[data.key];
      // actual data that is used when apply filters
      data.treeSelectionData = !isEmpty(deepnessKeyMapping.current) ? buildTreeSelectionData(seletedNodes, deepnessKeyMapping.current) : [];
    }
    CollectData(data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    textValue,
    selectedKey,
    selectedValue,
    selectedOperator,
    selectedValueKeys,
    seletedNodes,
  ]);

  useEffect(() => {
    dataModel.subscribe('Change', (data) => {
      if (type === Types.FreeText) {
        setTextValue(data.value);
      } else if (type === Types.TreeSelection) {
        // prefill tree selection
        setTreeNodes(buildTreeNodesFromValueOptions(data.valueOptions));
      } else {
        // update the single-select
        setSelectedValueItem({
          key: data.value,
          text: data.valueText,
        });
        // update the multiselect
        setSelectedValues(data.valueKeys);
      }
      if (enableOperator) {
        setSelectedOperator(data.operator);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTextChange = (e) => {
    setTextValue(e.target.value);
  };

  const onKeyChange = (_, item = {}) => {
    setSelectedKey(item);
  };

  const onValueChange = (_, item = {}) => {
    setSelectedValueItem(item);
  };

  const onMultiValueChange = useCallback((_, item = {}) => {
    setSelectedValueItem(item);
    setSelectedValues(getKeys(item));
  }, [getKeys]);

  const onTreeNodesChange = useCallback((data) => {
    setSelectedNodes(data.mergedSelectedNodes);
  }, []);

  const onDelete = () => {
    onFilterOptionsItemDeleted({ key: itemKey });
  };

  const onOperatorChange = (_, item = {}) => {
    setSelectedOperator(item);
  };

  const errorMessageMerged = errorMessage.join('\n') || null;

  const helpText = enableMultiSelect ? `${itemKey} 1\n${itemKey} 2\n${itemKey} n` : '';

  const renderOperandValueComponent = useCallback(() => {
    if (type === Types.FreeText) {
      return (
        <TextField
          className="FilterOptionItemDropdownValue"
          disabled={disabled}
          inputClassName={classNames({ [TextArea]: enableMultiSelect })}
          onChange={onTextChange}
          value={textValue}
          errorMessage={errorMessageMerged}
          multiline={enableMultiSelect}
          autoAdjustHeight={enableMultiSelect}
          placeholder={helpText}
        />
      );
    }
    if (type === Types.TreeSelection) {
      return (
        <DropdownTreeViewSelector
          treeViewProps={{
            nodes: treeNodes,
            onChange: setTreeNodes,
            maxHeight: 500,
            cellHeight: 33,
          }}
          dropdownProps={{
            disabled,
            dropdownWidth: '500px',
            errorMessage: errorMessageMerged,
            className: ['FilterOptionItemDropdownValue', 'treeViewDropdown'],
          }}
          searchBoxPlaceholder={i18nLegacy.getString('ProductCondition_TreeView_Search')}
          onChange={onTreeNodesChange}
        />
      );
    }
    if (enableMultiSelect) {
      return (
        <Dropdown
          className="FilterOptionItemDropdownValue"
          multiSelect
          multiSelectDelimiter=", "
          errorMessage={errorMessageMerged}
          selectedKeys={selectedValueKeys ||
                (selectedValue ? selectedValue.key : valueOptionsDefaultSelectedKey)}
          onChange={onMultiValueChange}
          options={valueOptions}
          disabled={disabled}
        />
      );
    }
    return (
      <Dropdown
        className="FilterOptionItemDropdownValue"
        errorMessage={errorMessageMerged}
        selectedKey={selectedValue ? selectedValue.key : valueOptionsDefaultSelectedKey}
        onChange={onValueChange}
        options={valueOptions}
        disabled={disabled}
      />
    );
  }, [TextArea, disabled, enableMultiSelect, errorMessageMerged, i18nLegacy, onMultiValueChange, onTreeNodesChange, selectedValue, selectedValueKeys, textValue, treeNodes, type, valueOptions, valueOptionsDefaultSelectedKey]);

  return (
    <div className={Container} id={`FilterOptionItem_${itemKey}`}>
      <div className={OperandContainer}>
        <Dropdown
          className="FilterOptionItemDropdownKey"
          selectedKey={selectedKey ? selectedKey.key : keyOptionsDefaultSelectedKey}
          // eslint-disable-next-line react/jsx-no-bind
          onChange={onKeyChange}
          options={keyOptions}
          disabled={disabled}
        />
      </div>
      <div className={OperatorContainer}>
        {
          enableOperator ?
            <Dropdown
              className="FilterOptionItemDropdownOperator"
              selectedKey={selectedOperator ? selectedOperator.key : operatorDefaultSelectedKey}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={onOperatorChange}
              options={operatorOptions}
              disabled={disabled}
              style={
                {
                  width: 64,
                  marginLeft: 12,
                  marginRight: 12,
                }
              }
            />
            :
            <span className="defaultOperator">=</span>
        }
      </div>
      <div className={OperandValueContainer}>
        { renderOperandValueComponent() }
      </div>
      { deletable ?
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <div role="button" tabIndex={0} className={DeleteButtonContainer} onClick={onDelete}>
          <span className="close" />
        </div> : null
      }
    </div>
  );
};

FilterOptionsItemUnstyled.propTypes = {
  classes: PropTypes.shape(mapObject(
    getContainerFactoryStyles(),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  i18nLegacy: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  type: PropTypes.oneOf(types),
  disabled: PropTypes.bool,
  deletable: PropTypes.bool,
  itemKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onFilterOptionsItemDeleted: PropTypes.func,
  CollectData: PropTypes.func.isRequired,
  errorMessage: PropTypes.arrayOf(PropTypes.string),
  keyOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  })).isRequired,
  operatorOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  })).isRequired,
  valueOptions: PropTypes.arrayOf(PropTypes.shape({})),
  keyOptionsDefaultSelectedKey: PropTypes.string,
  valueOptionsDefaultSelectedKey: PropTypes.string,
  operatorDefaultSelectedKey: PropTypes.string,
  dataModel: PropTypes.shape({
    subscribe: PropTypes.func,
  }).isRequired,
  enableOperator: PropTypes.bool,
  enableMultiSelect: PropTypes.bool,
};

FilterOptionsItemUnstyled.defaultProps = {
  type: Types.FreeText,
  disabled: false,
  deletable: false,
  onFilterOptionsItemDeleted: noop,
  errorMessage: [],
  valueOptions: null,
  keyOptionsDefaultSelectedKey: undefined,
  valueOptionsDefaultSelectedKey: undefined,
  operatorDefaultSelectedKey: 'equal',
  enableOperator: false,
  enableMultiSelect: false,
};

const FilterOptionsItem =
  withDefaultStyles(FilterOptionsItemUnstyled, getContainerFactoryStyles);

export {
  FilterOptionsItem,
};
